import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { data } from '../bridge/client';
import type { SidebarSnapshot } from '../bridge/protocol';
import { ChannelStep } from '../core/channelStep';
import { ConnectionBanner, type WebSocketConnectionState } from '../core/connectionBanner';
import { TeammateNameDisplay } from '../core/teammateNameDisplay';
import { TypingTracker, holdMillis } from '../core/typingTracker';
import { tf, t } from '../i18n/i18n';
import { Conversation } from './conversation/Conversation';
import { ThreadPane } from './conversation/ThreadPane';
import { useLive, useLocalStorage } from './hooks';
import { buildSidebar, filterSections } from './models/sidebarModel';
import { beginChannelSwitch, markFirstMeaningfulPaint } from './perf';
import { prefetchPage } from './pageCache';
import { ProfileMenu } from './ProfileMenu';
import { Sidebar } from './sidebar/Sidebar';

const SIDEBAR_TABLES = ['teams', 'channels', 'channelMembers', 'categories', 'users', 'userStatuses', 'posts', 'drafts', 'syncState', 'channelParticipants'] as const;

export function Workspace({ serverBase, onSignedOut }: { serverBase: string; onSignedOut(): void }) {
  const { value: snapshot } = useLive<SidebarSnapshot>(() => data.call('sidebar'), [...SIDEBAR_TABLES], []);
  const [teamId, setTeamId] = useState<string | null>(null);
  const [selected, setSelected] = useState<{ id: string; boundary: number | null; previous: string | null } | null>(null);
  const [query, setQuery] = useState('');
  const [threadRoot, setThreadRoot] = useState<string | null>(null);
  const [profileAnchor, setProfileAnchor] = useState<DOMRect | null>(null);
  const [connection, setConnection] = useState<{ state: WebSocketConnectionState; since: number }>(() => ({
    state: (data.lastConnection?.state as WebSocketConnectionState | undefined) ?? { kind: 'connecting' },
    since: Date.now(),
  }));
  const [typingTick, setTypingTick] = useState(0);
  const tracker = useRef(new TypingTracker());
  const [sidebarWidth, setSidebarWidth] = useLocalStorage<string>('satori.sidebarWidth', '296');
  const [threadWidth, setThreadWidth] = useLocalStorage<string>('satori.threadWidth', '420');
  const restored = useRef(false);

  // The name policy belongs to the READER, resolved once per snapshot (TeammateNameDisplay).
  useMemo(() => {
    if (!snapshot) return;
    TeammateNameDisplay.setCurrent(
      TeammateNameDisplay.resolveFormat(
        snapshot.syncState.name_display_config ?? null,
        snapshot.syncState.name_display_locked ?? null,
        snapshot.syncState.name_display_preference ?? null,
      ),
    );
  }, [snapshot]);

  const [clock, setClock] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setClock(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);
  const model = useMemo(() => (snapshot ? buildSidebar(snapshot, teamId, clock) : null), [snapshot, teamId, clock]);

  // Restore where the reader left off (boot path step 2: the last screen, from the store).
  useEffect(() => {
    if (!snapshot || restored.current || !snapshot.teams.length) return;
    restored.current = true;
    const lastTeam = snapshot.syncState.last_team_id;
    const team = snapshot.teams.find((t) => t.id === lastTeam) ?? snapshot.teams[0];
    setTeamId(team.id);
    const lastChannel = snapshot.syncState.last_channel_id;
    if (lastChannel && snapshot.channels.some((c) => c.id === lastChannel && !c.deleteAt)) setSelected({ id: lastChannel, boundary: null, previous: null });
  }, [snapshot]);

  useEffect(() => {
    if (model && (model.sections.length || model.firstRun)) markFirstMeaningfulPaint(`${model.sections.reduce((n, s) => n + s.rows.length, 0)} rows`);
  }, [model]);

  // Transient signals: typing pulses and the connection state.
  useEffect(() => {
    // A signal can land between this component's first render and this subscription: re-read
    // the newest connection state once subscribed, or the strip could say "Conectando…" for ever.
    const off = data.onSignal((s) => {
        if (s.kind === 'typing') {
          if (s.parentId.startsWith('__clear__')) tracker.current.clear(s.userId, s.channelId, s.parentId.slice(9));
          else tracker.current.note(s.userId, s.channelId, s.parentId, Date.now());
          setTypingTick((n) => n + 1);
        } else if (s.kind === 'connection') {
          setConnection({ state: s.state as WebSocketConnectionState, since: Date.now() });
        } else if (s.kind === 'authExpired') {
          onSignedOut();
        }
      });
    if (data.lastConnection) setConnection({ state: data.lastConnection.state as WebSocketConnectionState, since: Date.now() });
    return off;
  }, [onSignedOut]);
  useEffect(() => {
    const id = setInterval(() => {
      if (tracker.current.prune(Date.now())) setTypingTick((n) => n + 1);
    }, 2000);
    return () => clearInterval(id);
  }, []);
  // Re-evaluate the banner after its grace period even if nothing else changes.
  const [, setBannerTick] = useState(0);
  useEffect(() => {
    const id = setTimeout(() => setBannerTick((n) => n + 1), ConnectionBanner.graceMillis + 50);
    return () => clearTimeout(id);
  }, [connection]);

  // Visibility: a tab coming back to the front catches up (the "foreground" of a browser).
  useEffect(() => {
    const onVisible = () => document.visibilityState === 'visible' && void data.call('foreground');
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  const selectChannel = useCallback(
    (channelId: string) => {
      if (selected?.id === channelId) return;
      beginChannelSwitch(channelId);
      // Ask the worker for the page NOW, before React re-renders anything.
      void prefetchPage(channelId);
      // The unread separator needs the PRE-clear viewed-at, captured before the open clears it.
      const member = snapshot?.members.find((m) => m.channelId === channelId);
      const channel = snapshot?.channels.find((c) => c.id === channelId);
      const boundary = member && channel && channel.lastPostAt > member.lastViewedAt ? member.lastViewedAt : null;
      setSelected({ id: channelId, boundary, previous: selected?.id ?? null });
      setThreadRoot(null);
    },
    [selected?.id, snapshot],
  );

  const selectTeam = useCallback((id: string) => {
    setTeamId(id);
    void data.call('setSyncValues', { last_team_id: id });
  }, []);

  // ⌥↑ / ⌥↓ (Alt on Windows/Linux): the conversation above / below, wrapping, over the rows the
  // sidebar is DRAWING (category collapse, the filter, the unread lens all count).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!e.altKey || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') || !model) return;
      e.preventDefault();
      const sections = filterSections(model.sections, query, false);
      const next = ChannelStep.target(sections, query.trim() !== '', selected?.id ?? null, e.key === 'ArrowUp' ? 'previous' : 'next');
      if (next) {
        selectChannel(next);
        document.querySelector(`[data-channel-id="${next}"]`)?.scrollIntoView({ block: 'nearest' });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [model, query, selected?.id, selectChannel]);

  const channel = selected ? model?.channelsById.get(selected.id) ?? null : null;
  const row = useMemo(() => model?.sections.flatMap((s) => s.rows).find((r) => r.id === selected?.id) ?? null, [model, selected?.id]);
  const me = snapshot?.currentUserId ?? '';

  let subtitle = '';
  if (channel?.type === 'D' && row?.dmPartnerUsername) subtitle = '@' + row.dmPartnerUsername;
  else if (channel?.header) subtitle = channel.header;
  let readOnly: string | null = null;
  if (channel?.deleteAt) readOnly = t('channel.archived.readOnly');
  else if (row?.inactive === 'deactivatedPartner') readOnly = tf('channel.deactivatedPartner.readOnly', row.dmPartnerName ?? '');

  const banner = ConnectionBanner.decide(connection.state, Date.now() - connection.since);
  const strip =
    banner.kind === 'none'
      ? null
      : banner.kind === 'connecting'
        ? t('channel.status.connecting')
        : ConnectionBanner.showsAttempt(banner)
          ? tf('channel.status.reconnectingAttempt', banner.attempt)
          : t('channel.status.reconnecting');

  void typingTick;
  const typists = selected ? tracker.current.typistIds(selected.id, '', Date.now()) : [];
  void holdMillis;

  const startResize = (which: 'sidebar' | 'thread') => (e: React.PointerEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const start = Number(which === 'sidebar' ? sidebarWidth : threadWidth);
    const move = (ev: PointerEvent) => {
      const delta = ev.clientX - startX;
      if (which === 'sidebar') setSidebarWidth(String(Math.max(240, Math.min(420, start + delta))));
      else setThreadWidth(String(Math.max(320, Math.min(640, start - delta))));
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <div className={`workspace${selected ? ' has-selection' : ''}`} style={{ ['--sidebar-width' as string]: `${sidebarWidth}px` }}>
      <Sidebar
        model={model}
        teamId={teamId}
        selectedChannelId={selected?.id ?? null}
        onSelectTeam={selectTeam}
        onSelectChannel={selectChannel}
        onToggleCategory={(id, collapsed) => void data.call('setCategoryCollapsed', id, collapsed)}
        onMarkRead={(id) => void data.call('markChannelRead', id)}
        onOpenProfile={(rect) => setProfileAnchor(rect)}
        query={query}
        onQuery={setQuery}
        serverBase={serverBase}
      />
      <div className="splitter" onPointerDown={startResize('sidebar')} role="separator" aria-orientation="vertical" />
      {selected && channel ? (
        <Conversation
          key={selected.id}
          channelId={selected.id}
          previousChannelId={selected.previous}
          title={row?.name ?? channel.displayName}
          subtitle={subtitle}
          icon={channel.type === 'P' ? 'lock' : channel.type === 'O' ? 'hash' : channel.type === 'G' ? 'group' : 'dm'}
          unreadBoundary={selected.boundary}
          me={me}
          myUsername={model?.me?.username ?? null}
          typists={typists}
          readOnlyNotice={readOnly}
          onOpenThread={setThreadRoot}
          onOpenUser={() => undefined}
          connectionStrip={strip}
        />
      ) : (
        <section className="conversation-pane">
          <div className="wallpaper" />
          <div className="empty-state">
            <h2>{t('mac.noConversationSelected')}</h2>
            <p>{t('mac.noConversationHint')}</p>
          </div>
        </section>
      )}
      {threadRoot ? (
        <>
          <div className="splitter" style={{ marginLeft: -4 }} onPointerDown={startResize('thread')} role="separator" aria-orientation="vertical" />
          <ThreadPane rootId={threadRoot} me={me} myUsername={model?.me?.username ?? null} onClose={() => setThreadRoot(null)} onOpenUser={() => undefined} width={Number(threadWidth)} />
        </>
      ) : null}
      {profileAnchor && model?.me ? (
        <ProfileMenu
          anchor={profileAnchor}
          onClose={() => setProfileAnchor(null)}
          onSignOut={async () => {
            await data.call('logout');
            onSignedOut();
          }}
        />
      ) : null}
    </div>
  );
}
